import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  api,
  CATEGORY_LABEL,
  type Customer,
  type ReportCategory,
  type Server,
  type UploadResult,
} from '../components/api'
import {
  Alert,
  Button,
  Card,
  FormField,
  Input,
  Select,
  Textarea,
} from '../components/ui'

type FormState = {
  customer_id: string
  server_id: string
  category: ReportCategory
  body: string
  work_minutes: string
  occurred_at: string
}

function nowLocalISO() {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function ReportNewPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialCustomerId = searchParams.get('customer_id') ?? ''

  const [customers, setCustomers] = useState<Customer[]>([])
  const [servers, setServers] = useState<Server[]>([])
  const [form, setForm] = useState<FormState>({
    customer_id: initialCustomerId,
    server_id: '',
    category: 'routine',
    body: '',
    work_minutes: '0',
    occurred_at: nowLocalISO(),
  })
  const [files, setFiles] = useState<File[]>([])
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    api<{ items: Customer[] }>('/api/customers')
      .then((d) => {
        if (!cancelled) setCustomers(d.items ?? [])
      })
      .catch(() => {
        /* 顧客リスト取得失敗時は手入力 */
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!form.customer_id) {
      setServers([])
      return
    }
    let cancelled = false
    api<{ items: Server[] }>(`/api/customers/${form.customer_id}/servers`)
      .then((d) => {
        if (!cancelled) setServers(d.items ?? [])
      })
      .catch(() => {
        if (!cancelled) setServers([])
      })
    return () => {
      cancelled = true
    }
  }, [form.customer_id])

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const { [key]: _, ...rest } = prev
      return rest
    })
  }

  const validate = () => {
    const errors: Record<string, string> = {}
    if (!form.customer_id) errors.customer_id = '顧客を選択してください'
    if (!form.body.trim()) errors.body = '対応内容は必須です'
    if (!form.occurred_at) errors.occurred_at = '発生日時は必須です'
    const wm = Number(form.work_minutes)
    if (Number.isNaN(wm) || wm < 0) errors.work_minutes = '0 以上の数値で'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const uploadFile = async (file: File): Promise<string> => {
    const res = await api<UploadResult>('/api/uploads', {
      method: 'POST',
      raw: true,
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'X-Filename': encodeURIComponent(file.name),
      },
    })
    return res.key
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      const keys: string[] = []
      for (let i = 0; i < files.length; i++) {
        setUploadingIndex(i)
        const key = await uploadFile(files[i])
        keys.push(key)
      }
      setUploadingIndex(null)

      await api<{ id: number }>('/api/reports', {
        method: 'POST',
        body: {
          customer_id: Number(form.customer_id),
          server_id: form.server_id ? Number(form.server_id) : null,
          category: form.category,
          body: form.body,
          work_minutes: Number(form.work_minutes) || 0,
          attachments_r2_paths: keys.length > 0 ? keys : undefined,
          occurred_at: new Date(form.occurred_at).toISOString(),
        },
      })
      navigate(`/customers/${form.customer_id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました')
    } finally {
      setSubmitting(false)
      setUploadingIndex(null)
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>対応履歴を新規登録</h1>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <form onSubmit={handleSubmit} noValidate>
        <Card title="基本情報">
          <div className="form-row">
            <FormField
              label="顧客"
              htmlFor="customer_id"
              required
              error={fieldErrors.customer_id}
            >
              <Select
                id="customer_id"
                value={form.customer_id}
                onChange={(e) => {
                  update('customer_id', e.target.value)
                  update('server_id', '')
                }}
                required
              >
                <option value="">選択してください</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="サーバー（任意）" htmlFor="server_id">
              <Select
                id="server_id"
                value={form.server_id}
                onChange={(e) => update('server_id', e.target.value)}
                disabled={servers.length === 0}
              >
                <option value="">紐付けなし</option>
                {servers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.kind} {s.plan ? `／${s.plan}` : ''}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="カテゴリ" htmlFor="category" required>
              <Select
                id="category"
                value={form.category}
                onChange={(e) =>
                  update('category', e.target.value as ReportCategory)
                }
              >
                {(Object.keys(CATEGORY_LABEL) as ReportCategory[]).map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="発生日時"
              htmlFor="occurred_at"
              required
              error={fieldErrors.occurred_at}
            >
              <Input
                id="occurred_at"
                type="datetime-local"
                value={form.occurred_at}
                onChange={(e) => update('occurred_at', e.target.value)}
                required
              />
            </FormField>
            <FormField
              label="工数（分）"
              htmlFor="work_minutes"
              error={fieldErrors.work_minutes}
            >
              <Input
                id="work_minutes"
                type="number"
                min="0"
                step="1"
                value={form.work_minutes}
                onChange={(e) => update('work_minutes', e.target.value)}
              />
            </FormField>
          </div>
        </Card>

        <Card title="内容">
          <FormField
            label="対応内容"
            htmlFor="body"
            required
            error={fieldErrors.body}
            hint="経緯・実施した対応・残課題などを記録"
          >
            <Textarea
              id="body"
              value={form.body}
              onChange={(e) => update('body', e.target.value)}
              rows={10}
              required
            />
          </FormField>
        </Card>

        <Card title="添付ファイル">
          <FormField
            label="ファイル（任意・複数可、各25MBまで）"
            htmlFor="files"
            hint="スクリーンショット・ログ・メール本文など"
          >
            <input
              id="files"
              type="file"
              multiple
              onChange={(e) =>
                setFiles(Array.from(e.currentTarget.files ?? []))
              }
            />
          </FormField>
          {files.length > 0 && (
            <ul
              className="stack-2"
              style={{ padding: 0, margin: 0, listStyle: 'none' }}
            >
              {files.map((f, i) => (
                <li key={i} className="muted" style={{ fontSize: 12 }}>
                  {uploadingIndex === i ? '↑ アップロード中…' : '・'} {f.name}
                  （{Math.round(f.size / 1024)} KB）
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(-1)}
          >
            キャンセル
          </Button>
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting
              ? uploadingIndex !== null
                ? `アップロード中（${uploadingIndex + 1}/${files.length}）…`
                : '保存中…'
              : '保存'}
          </Button>
        </div>
      </form>
    </>
  )
}
